import { TableSkeleton } from "@/components/ui/skeleton-v2";
import type { Family } from "@/lib/family";
import { FeatureImportance } from "./feature-importance";
import { ForecastDiagnosticsPanel } from "./forecast-diagnostics";
import { Leaderboard } from "./leaderboard";
import { useFeatureImportance, useForecastDiagnostics, useLeaderboard } from "./queries";

interface AnalyticsTabProps {
  modelId: string;
  revisionNo: number;
  family: Family | null;
}

export function AnalyticsTab({ modelId, revisionNo, family }: AnalyticsTabProps) {
  const leaderboardQuery = useLeaderboard(modelId, revisionNo);
  const featureImportanceQuery = useFeatureImportance(
    modelId,
    family === "tabular" ? revisionNo : null,
  );
  const forecastDiagnosticsQuery = useForecastDiagnostics(
    modelId,
    family === "forecast" ? revisionNo : null,
  );

  return (
    <div className="space-y-6">
      {/* Leaderboard - shown for all families */}
      <section>
        <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">
          Leaderboard
        </h2>
        {leaderboardQuery.isLoading ? (
          <TableSkeleton rows={5} cols={4} />
        ) : leaderboardQuery.error ? (
          <div className="rounded-[14px] border border-line bg-surface p-5 text-center text-sm text-muted">
            Failed to load leaderboard
          </div>
        ) : leaderboardQuery.data ? (
          <Leaderboard data={leaderboardQuery.data} />
        ) : null}
      </section>

      {/* Feature Importance - tabular only */}
      {family === "tabular" && (
        <section>
          <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">
            Feature Importance
          </h2>
          {featureImportanceQuery.isLoading ? (
            <div className="h-[400px] animate-pulse rounded-[14px] border border-line bg-surface" />
          ) : featureImportanceQuery.error ? (
            <div className="rounded-[14px] border border-line bg-surface p-5 text-center text-sm text-muted">
              Feature importance not available
            </div>
          ) : featureImportanceQuery.data ? (
            <FeatureImportance data={featureImportanceQuery.data} />
          ) : null}
        </section>
      )}

      {/* Forecast Diagnostics - forecast only */}
      {family === "forecast" && (
        <section>
          <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">
            Forecast Diagnostics
          </h2>
          {forecastDiagnosticsQuery.isLoading ? (
            <div className="h-[400px] animate-pulse rounded-[14px] border border-line bg-surface" />
          ) : forecastDiagnosticsQuery.error ? (
            <div className="rounded-[14px] border border-line bg-surface p-5 text-center text-sm text-muted">
              Forecast diagnostics not available
            </div>
          ) : forecastDiagnosticsQuery.data ? (
            <ForecastDiagnosticsPanel data={forecastDiagnosticsQuery.data} />
          ) : null}
        </section>
      )}
    </div>
  );
}
